import { useState, useRef } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useGlobalMedicines,
  useBusinessMedicines,
  useLinkMedicine,
  useUnlinkMedicine,
  useUpdateBusinessMedicine,
} from "@/hooks/useAdminMedicines";
import {
  usePremiumServices,
  useCreatePremiumService,
  useUpdatePremiumService,
  useDeletePremiumService,
  useUploadPremiumServicePhoto,
} from "@/hooks/useAdminPremiumServices";
import {
  useSpecialists,
  useCreateSpecialist,
  useUpdateSpecialist,
  useDeleteSpecialist,
} from "@/hooks/useAdminSpecialists";
import { SpecialistRole, VisitFrequency } from "@postnatal-care/shared-types";
import { Trash2, Plus, Edit, ShieldAlert } from "lucide-react";

interface Props {
  businessId: string;
}

export const CareMedicalTab = ({ businessId }: Props) => {
  const [activeSubTab, setActiveSubTab] = useState("medicines");

  return (
    <Card>
      <CardHeader>
        <CardTitle>Care Services &amp; Medical Management</CardTitle>
        <CardDescription>
          Manage postnatal care medicines, highlightable premium services, and
          visiting doctors &amp; therapists.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs
          value={activeSubTab}
          onValueChange={setActiveSubTab}
          className="space-y-4"
        >
          <TabsList className="grid grid-cols-3 w-[450px]">
            <TabsTrigger value="medicines">Medicines</TabsTrigger>
            <TabsTrigger value="premium-services">Premium Services</TabsTrigger>
            <TabsTrigger value="specialists">
              Doctors &amp; Therapists
            </TabsTrigger>
          </TabsList>

          <TabsContent value="medicines" className="pt-2">
            <MedicinesSection businessId={businessId} />
          </TabsContent>

          <TabsContent value="premium-services" className="pt-2">
            <PremiumServicesSection businessId={businessId} />
          </TabsContent>

          <TabsContent value="specialists" className="pt-2">
            <SpecialistsSection businessId={businessId} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

// ============================================
// MEDICINES COMPONENT
// ============================================
const MedicinesSection = ({ businessId }: { businessId: string }) => {
  const { data: globals } = useGlobalMedicines();
  const { data: linked, isLoading } = useBusinessMedicines(businessId);
  const linkMut = useLinkMedicine(businessId);
  const updateMut = useUpdateBusinessMedicine(businessId);
  const unlinkMut = useUnlinkMedicine(businessId);

  const [selectedMedId, setSelectedMedId] = useState("");
  const [dosageEn, setDosageEn] = useState("");
  const [dosageMl, setDosageMl] = useState("");
  const [freqEn, setFreqEn] = useState("");
  const [freqMl, setFreqMl] = useState("");
  const [isPaid, setIsPaid] = useState(false);
  const [price, setPrice] = useState("");
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);

  const handleEditInit = (l: any) => {
    setEditingLinkId(l.id);
    setSelectedMedId(l.medicineId || "");
    setDosageEn(l.dosageInstructionsEn || "");
    setDosageMl(l.dosageInstructionsMl || "");
    setFreqEn(l.frequencyEn || "");
    setFreqMl(l.frequencyMl || "");
    setIsPaid(l.isPaid);
    setPrice(l.price ? String(l.price) : "");
  };

  const handleCancelEdit = () => {
    setEditingLinkId(null);
    setSelectedMedId("");
    setDosageEn("");
    setDosageMl("");
    setFreqEn("");
    setFreqMl("");
    setIsPaid(false);
    setPrice("");
  };

  const handleLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMedId) return toast.error("Please select a medicine");
    try {
      if (editingLinkId) {
        await updateMut.mutateAsync({
          linkId: editingLinkId,
          data: {
            dosageInstructionsEn: dosageEn || undefined,
            dosageInstructionsMl: dosageMl || undefined,
            frequencyEn: freqEn || undefined,
            frequencyMl: freqMl || undefined,
            isPaid,
            price: price ? Number(price) : undefined,
          },
        });
        toast.success("Medicine association updated successfully");
      } else {
        await linkMut.mutateAsync({
          medicineId: selectedMedId,
          dosageInstructionsEn: dosageEn || undefined,
          dosageInstructionsMl: dosageMl || undefined,
          frequencyEn: freqEn || undefined,
          frequencyMl: freqMl || undefined,
          isPaid,
          price: price ? Number(price) : undefined,
        });
        toast.success("Medicine associated successfully");
      }
      handleCancelEdit();
    } catch (err: any) {
      toast.error(err.message || "Failed to link/update medicine");
    }
  };

  const handleUnlink = async (linkId: string) => {
    if (!confirm("Are you sure you want to remove this medicine association?"))
      return;
    try {
      await unlinkMut.mutateAsync(linkId);
      toast.success("Medicine removed");
      if (editingLinkId === linkId) {
        handleCancelEdit();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to remove medicine");
    }
  };

  return (
    <div className="space-y-6">
      <form
        onSubmit={handleLink}
        className="bg-muted/30 p-4 rounded-lg border space-y-4"
      >
        <h4 className="font-semibold text-sm">
          {editingLinkId
            ? "Edit Associated Medicine"
            : "Associate New Medicine"}
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Select Medicine</Label>
            <Select
              value={selectedMedId}
              onValueChange={setSelectedMedId}
              disabled={!!editingLinkId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a medicine" />
              </SelectTrigger>
              <SelectContent>
                {globals?.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.nameEn} ({m.nameMl})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Price (if paid)</Label>
            <Input
              type="number"
              placeholder="e.g. 150"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              disabled={!isPaid}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Dosage (EN)</Label>
            <Input
              placeholder="e.g. 1 tablet after food"
              value={dosageEn}
              onChange={(e) => setDosageEn(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Dosage (ML)</Label>
            <Input
              placeholder="ഭക്ഷണത്തിന് ശേഷം 1 ഗുളിക"
              value={dosageMl}
              onChange={(e) => setDosageMl(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Frequency (EN)</Label>
            <Input
              placeholder="e.g. Morning and Night"
              value={freqEn}
              onChange={(e) => setFreqEn(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Frequency (ML)</Label>
            <Input
              placeholder="രാവിലെയും രാത്രിയും"
              value={freqMl}
              onChange={(e) => setFreqMl(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="paid-switch"
            checked={isPaid}
            onCheckedChange={setIsPaid}
          />
          <Label htmlFor="paid-switch">Paid Medicine (charges apply)</Label>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="submit"
            disabled={linkMut.isPending || updateMut.isPending}
          >
            {editingLinkId ? (
              <Edit className="w-4 h-4 mr-2" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            {editingLinkId ? "Update Association" : "Associate Medicine"}
          </Button>
          {editingLinkId && (
            <Button type="button" variant="outline" onClick={handleCancelEdit}>
              Cancel
            </Button>
          )}
        </div>
      </form>

      <div className="space-y-2">
        <h4 className="font-semibold text-sm">Associated Medicines</h4>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">
            Loading associated medicines...
          </p>
        ) : linked?.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No medicines associated yet.
          </p>
        ) : (
          <div className="border rounded-md divide-y">
            {linked?.map((l) => (
              <div
                key={l.id}
                className="p-4 flex items-center justify-between gap-4"
              >
                <div>
                  <p className="font-medium text-sm">
                    {l.medicine?.nameEn}{" "}
                    <span className="text-muted-foreground">
                      ({l.medicine?.nameMl})
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Dosage: {l.dosageInstructionsEn || "None"} | Frequency:{" "}
                    {l.frequencyEn || "None"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Dosage (ML): {l.dosageInstructionsMl || "None"} | Frequency
                    (ML): {l.frequencyMl || "None"}
                  </p>
                  {l.isPaid && (
                    <p className="text-xs text-emerald-600 font-semibold mt-1">
                      Paid: ₹{Number(l.price)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEditInit(l)}
                  >
                    <Edit className="w-4 h-4 text-primary" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleUnlink(l.id)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// PREMIUM SERVICES COMPONENT
// ============================================
const PremiumServicesSection = ({ businessId }: { businessId: string }) => {
  const { data: services, isLoading } = usePremiumServices(businessId);
  const createMut = useCreatePremiumService(businessId);
  const updateMut = useUpdatePremiumService(businessId);
  const deleteMut = useDeletePremiumService(businessId);
  const uploadPhotoMut = useUploadPremiumServicePhoto(businessId);

  const [nameEn, setNameEn] = useState("");
  const [nameMl, setNameMl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [price, setPrice] = useState("");
  const [highlighted, setHighlighted] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleEditInit = (s: any) => {
    setEditingId(s.id);
    setNameEn(s.nameEn || "");
    setNameMl(s.nameMl || "");
    setImageUrl(s.imageUrl || "");
    setPrice(s.price ? String(s.price) : "");
    setHighlighted(!!s.isHighlighted);
    setSelectedFile(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setNameEn("");
    setNameMl("");
    setImageUrl("");
    setSelectedFile(null);
    setPrice("");
    setHighlighted(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nameEn || !nameMl)
      return toast.error("English and Malayalam names are required");
    try {
      let serviceId = editingId;
      if (editingId) {
        await updateMut.mutateAsync({
          id: editingId,
          data: {
            nameEn,
            nameMl,
            price: price ? Number(price) : undefined,
            isHighlighted: highlighted,
          },
        });
        toast.success("Premium service updated");
      } else {
        const res = await createMut.mutateAsync({
          businessId,
          nameEn,
          nameMl,
          price: price ? Number(price) : undefined,
          isHighlighted: highlighted,
          isActive: true,
        });
        serviceId = res.id;
        toast.success("Premium service created");
      }

      if (selectedFile && serviceId) {
        await uploadPhotoMut.mutateAsync({ id: serviceId, file: selectedFile });
        toast.success("Photo uploaded successfully");
      }

      handleCancelEdit();
    } catch (err: any) {
      toast.error(err.message || "Failed to save service");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this service?")) return;
    try {
      await deleteMut.mutateAsync(id);
      toast.success("Service deleted");
      if (editingId === id) {
        handleCancelEdit();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to delete service");
    }
  };

  return (
    <div className="space-y-6">
      <form
        onSubmit={handleSubmit}
        className="bg-muted/30 p-4 rounded-lg border space-y-4"
      >
        <h4 className="font-semibold text-sm">
          {editingId
            ? "Edit Premium Service / Highlight"
            : "Add New Premium Service / Highlight"}
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Service Name (EN)</Label>
            <Input
              placeholder="e.g. Medicated Herbal Bath"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Service Name (ML)</Label>
            <Input
              placeholder="e.g. മരുന്ന് കുളി"
              value={nameMl}
              onChange={(e) => setNameMl(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Photo</Label>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setSelectedFile(file);
                }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadPhotoMut.isPending}
              >
                {selectedFile ? "Change Image" : "Select Image"}
              </Button>
              {selectedFile && (
                <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                  {selectedFile.name}
                </span>
              )}
              {!selectedFile && imageUrl && (
                <span className="text-xs text-green-600">
                  Has existing photo
                </span>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Extra Cost / Price (Optional)</Label>
            <Input
              type="number"
              placeholder="e.g. 2500"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="highlight-switch"
            checked={highlighted}
            onCheckedChange={setHighlighted}
          />
          <Label htmlFor="highlight-switch">
            Highlight on Business Profile
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="submit"
            disabled={
              createMut.isPending ||
              updateMut.isPending ||
              uploadPhotoMut.isPending
            }
          >
            {editingId ? (
              <Edit className="w-4 h-4 mr-2" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            {editingId ? "Update Service" : "Add Service"}
          </Button>
          {editingId && (
            <Button type="button" variant="outline" onClick={handleCancelEdit}>
              Cancel
            </Button>
          )}
        </div>
      </form>

      <div className="space-y-2">
        <h4 className="font-semibold text-sm">
          Current Services &amp; Highlights
        </h4>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading services...</p>
        ) : services?.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No services added yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services?.map((s) => (
              <Card
                key={s.id}
                className={
                  s.isHighlighted ? "border-amber-500/50 bg-amber-500/5" : ""
                }
              >
                <CardHeader className="p-4 flex flex-row items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                      {s.nameEn}
                      {s.isHighlighted && (
                        <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-normal">
                          Highlighted
                        </span>
                      )}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {s.nameMl}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-primary"
                      onClick={() => handleEditInit(s)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => handleDelete(s.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0 text-xs space-y-2">
                  {s.imageUrl && (
                    <img
                      src={s.imageUrl}
                      alt={s.nameEn}
                      className="w-full h-32 object-cover rounded-md border mb-2"
                    />
                  )}
                  {s.price && (
                    <p className="font-semibold text-emerald-600">
                      ₹{Number(s.price)}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// SPECIALISTS COMPONENT
// ============================================
const SpecialistsSection = ({ businessId }: { businessId: string }) => {
  const { data: specialists, isLoading } = useSpecialists(businessId);
  const createMut = useCreateSpecialist(businessId);
  const updateMut = useUpdateSpecialist(businessId);
  const deleteMut = useDeleteSpecialist(businessId);

  const [name, setName] = useState("");
  const [role, setRole] = useState<SpecialistRole>(SpecialistRole.DOCTOR);
  const [specEn, setSpecEn] = useState("");
  const [specMl, setSpecMl] = useState("");
  const [bioEn, setBioEn] = useState("");
  const [bioMl, setBioMl] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [freq, setFreq] = useState<VisitFrequency>(VisitFrequency.WEEKLY);
  const [schedEn, setSchedEn] = useState("");
  const [schedMl, setSchedMl] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);

  const handleEditInit = (spec: any) => {
    setEditingId(spec.id);
    setName(spec.name || "");
    setRole(spec.role || SpecialistRole.DOCTOR);
    setSpecEn(spec.specialtyEn || "");
    setSpecMl(spec.specialtyMl || "");
    setBioEn(spec.bioEn || "");
    setBioMl(spec.bioMl || "");
    setPhotoUrl(spec.photoUrl || "");
    setFreq(spec.visitFrequency || VisitFrequency.WEEKLY);
    setSchedEn(spec.visitScheduleNotesEn || "");
    setSchedMl(spec.visitScheduleNotesMl || "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setName("");
    setRole(SpecialistRole.DOCTOR);
    setSpecEn("");
    setSpecMl("");
    setBioEn("");
    setBioMl("");
    setPhotoUrl("");
    setFreq(VisitFrequency.WEEKLY);
    setSchedEn("");
    setSchedMl("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !specEn || !specMl)
      return toast.error("Name and specialties are required");
    try {
      if (editingId) {
        await updateMut.mutateAsync({
          id: editingId,
          data: {
            name,
            role,
            specialtyEn: specEn,
            specialtyMl: specMl,
            bioEn: bioEn || undefined,
            bioMl: bioMl || undefined,
            photoUrl: photoUrl || undefined,
            visitFrequency: freq,
            visitScheduleNotesEn: schedEn || undefined,
            visitScheduleNotesMl: schedMl || undefined,
          },
        });
        toast.success("Specialist profile updated");
      } else {
        await createMut.mutateAsync({
          businessId,
          name,
          role,
          specialtyEn: specEn,
          specialtyMl: specMl,
          bioEn: bioEn || undefined,
          bioMl: bioMl || undefined,
          photoUrl: photoUrl || undefined,
          visitFrequency: freq,
          visitScheduleNotesEn: schedEn || undefined,
          visitScheduleNotesMl: schedMl || undefined,
          isActive: true,
        });
        toast.success("Specialist profile created");
      }
      handleCancelEdit();
    } catch (err: any) {
      toast.error(err.message || "Failed to save specialist");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this specialist profile?"))
      return;
    try {
      await deleteMut.mutateAsync(id);
      toast.success("Specialist deleted");
      if (editingId === id) {
        handleCancelEdit();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to delete specialist");
    }
  };

  return (
    <div className="space-y-6">
      <form
        onSubmit={handleSubmit}
        className="bg-muted/30 p-4 rounded-lg border space-y-4"
      >
        <h4 className="font-semibold text-sm">
          {editingId
            ? "Edit Doctor or Therapist"
            : "Add New Doctor or Therapist"}
        </h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              placeholder="e.g. Dr. Lakshmi Nair"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select
              value={role}
              onValueChange={(v) => setRole(v as SpecialistRole)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SpecialistRole.DOCTOR}>Doctor</SelectItem>
                <SelectItem value={SpecialistRole.THERAPIST}>
                  Therapist
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Visit Frequency</Label>
            <Select
              value={freq}
              onValueChange={(v) => setFreq(v as VisitFrequency)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={VisitFrequency.DAILY}>Daily</SelectItem>
                <SelectItem value={VisitFrequency.WEEKLY}>Weekly</SelectItem>
                <SelectItem value={VisitFrequency.ON_CALL}>
                  On Call / Needed
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Specialty (EN)</Label>
            <Input
              placeholder="e.g. Gynecologist"
              value={specEn}
              onChange={(e) => setSpecEn(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Specialty (ML)</Label>
            <Input
              placeholder="e.g. ഗൈനക്കോളജിസ്റ്റ്"
              value={specMl}
              onChange={(e) => setSpecMl(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Schedule Notes (EN)</Label>
            <Input
              placeholder="e.g. Visits on Tuesdays 4 PM"
              value={schedEn}
              onChange={(e) => setSchedEn(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Schedule Notes (ML)</Label>
            <Input
              placeholder="ചൊവ്വാഴ്ചകളിൽ വൈകുന്നേരം 4 മണിക്ക്"
              value={schedMl}
              onChange={(e) => setSchedMl(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Bio / Intro (EN)</Label>
            <Textarea
              placeholder="Brief bio..."
              value={bioEn}
              onChange={(e) => setBioEn(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Bio / Intro (ML)</Label>
            <Textarea
              placeholder="ചെറിയ വിവരണം..."
              value={bioMl}
              onChange={(e) => setBioMl(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Photo URL (Optional)</Label>
          <Input
            placeholder="https://..."
            value={photoUrl}
            onChange={(e) => setPhotoUrl(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="submit"
            disabled={createMut.isPending || updateMut.isPending}
          >
            {editingId ? (
              <Edit className="w-4 h-4 mr-2" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            {editingId ? "Update Specialist" : "Add Specialist"}
          </Button>
          {editingId && (
            <Button type="button" variant="outline" onClick={handleCancelEdit}>
              Cancel
            </Button>
          )}
        </div>
      </form>

      <div className="space-y-2">
        <h4 className="font-semibold text-sm">Visiting Specialists</h4>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">
            Loading specialists...
          </p>
        ) : specialists?.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No specialists registered yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {specialists?.map((spec) => (
              <Card key={spec.id}>
                <CardHeader className="p-4 flex flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    {spec.photoUrl ? (
                      <img
                        src={spec.photoUrl}
                        alt={spec.name}
                        className="w-12 h-12 rounded-full object-cover border"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                        {spec.name[0]}
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-sm font-semibold">
                        {spec.name}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground capitalize">
                        {spec.role?.toLowerCase()} &middot; {spec.specialtyEn}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-primary"
                      onClick={() => handleEditInit(spec)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => handleDelete(spec.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0 text-xs space-y-2">
                  <p className="text-muted-foreground">
                    {spec.bioEn || "No bio description"}
                  </p>
                  <p className="text-[11px] bg-primary/5 text-primary px-2 py-1 rounded w-fit">
                    Frequency:{" "}
                    <span className="font-semibold capitalize">
                      {spec.visitFrequency?.toLowerCase().replace("_", " ")}
                    </span>
                    {spec.visitScheduleNotesEn &&
                      ` (${spec.visitScheduleNotesEn})`}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
